/**
 * canvasConversationGate — **le Canvas ne s'ouvre que dans un fil confirmé**
 * (#1331, spec #1327 « Conversation confirmée avant tout Canvas »).
 *
 * ## Le fait manquant
 *
 * Une conversation neuve n'existe d'abord que dans le navigateur : l'UUID est
 * frappé par le client, l'URL le porte, la barre latérale affiche un fantôme
 * optimiste et la connexion temps réel peut déjà être en `streaming`. **Aucun
 * de ces quatre signaux ne prouve qu'une ligne existe en base**, et la paternité
 * locale du fil n'en prouve pas davantage — elle dit qui a envoyé le message,
 * pas ce que le serveur en a fait.
 *
 * Ouvrir un Canvas dans un fil non persisté produit un document sans
 * propriétaire : son brouillon part avec `conversationId: null`, personne ne le
 * rouvre dans son fil, et l'avocat croit son travail rangé quelque part. La
 * cible supprime ce cas ; le refus le rend impossible plutôt que de le
 * rattraper après coup par une adoption tardive.
 *
 * ## Les deux preuves, et pourquoi elles suffisent
 *
 * 1. **La confirmation temps réel** — `conversation_created` traduit en
 *    `conversation_confirmed`, émis après l'INSERT et enregistré dans
 *    `activeSessionsStore.confirmedConversations`. Elle arrive AVANT la
 *    persistance du message et bien avant la fin de la réponse d'Hector :
 *    l'ouverture n'attend donc pas l'agent.
 * 2. **La donnée serveur** — le détail REST du fil a répondu. C'est ce qui rend
 *    un fil existant ouvrable directement, sans nouveau message et sans
 *    nouvelle confirmation temps réel, y compris un ancien fil persisté qui ne
 *    porte aucun message (aucun test « au moins un message » n'est appliqué).
 *
 * Aucune des deux ne retire le fantôme de la barre latérale : celui-ci ne
 * disparaît qu'une fois la ligne observée par la liste globale, et le retirer
 * plus tôt rouvrirait l'incident du 2026-08-09 (composeur bloqué en
 * « Chargement de l'accès »).
 *
 * ## Pourquoi quatre états et pas un booléen
 *
 * « Pas encore confirmé » recouvre trois situations qui n'appellent pas la même
 * phrase : le fil n'a pas encore été créé, sa vérification est en cours, ou
 * elle a échoué. Les confondre ferait dire « Envoyez un premier message » à un
 * avocat qui vient d'ouvrir un fil vieux de trois mois — un capteur qui rend la
 * même valeur avec et sans le défaut, et une consigne fausse à l'écran.
 *
 * ⚠️ **Un signal ABSENT n'est pas un signal NÉGATIF, et `verifying` est
 * précisément l'état qui les sépare** (revue #1331). « Je ne trouve pas ce fil
 * dans mes confirmés » ne veut pas dire « ce fil n'existe pas » — mais il ne
 * veut pas dire non plus « on est en train de regarder ». `verifying` promet à
 * l'avocat que le Canvas s'ouvrira ensuite ; cette promesse n'est tenable que
 * si une lecture serveur est RÉELLEMENT en vol. C'est pourquoi l'entrée est
 * `isServerLookupPending` — l'état de la requête — et non « la requête n'a pas
 * (encore) échoué » : un premier envoi échoué retire le fantôme sans qu'aucune
 * preuve n'arrive jamais, et la porte doit alors dire l'indisponibilité, pas
 * une attente.
 */

/** Ce que l'écran sait de l'existence du fil visé par une ouverture Canvas. */
export type CanvasConversationGate =
  /** Le serveur a prouvé le fil : l'ouverture est permise. */
  | 'confirmed'
  /** Aucun fil, ou un fil encore purement local : il faut un premier message. */
  | 'awaiting_first_message'
  /** Un fil est nommé et sa donnée serveur est EN VOL : l'attente est réelle. */
  | 'verifying'
  /**
   * Rien ne prouve le fil et rien ne cherche à le prouver — lecture serveur
   * échouée, ou premier envoi échoué dont le fantôme a été retiré au rollback.
   */
  | 'unverifiable';

export interface CanvasConversationGateInput {
  /** Fil visé par le geste — `null` sur « Nouvelle conversation ». */
  conversationId: string | null;
  /** `conversation_confirmed` reçu pour CE fil dans cette session de navigateur. */
  isConfirmedInSession: boolean;
  /** Le fil est encore un fantôme optimiste : la liste globale ne l'a pas vu. */
  isPendingConversation: boolean;
  /** Le détail REST de CE fil a répondu — donnée serveur, pas cache d'affichage. */
  hasServerRecord: boolean;
  /**
   * Une lecture serveur de CE fil est en vol en ce moment.
   *
   * C'est ce qui autorise à promettre « le Canvas s'ouvrira ensuite ». Aucune
   * requête en cours et aucune preuve ⟹ rien ne viendra : la porte doit dire
   * l'indisponibilité plutôt qu'une attente qui ne finira pas.
   */
  isServerLookupPending: boolean;
}

/**
 * Résout l'état d'existence du fil visé par une ouverture Canvas.
 *
 * Fonction pure : elle ne lit aucun store et n'ouvre rien. Ses appelants lui
 * passent des faits déjà observés, ce qui permet de la rejouer entièrement
 * hors navigateur.
 */
export function resolveCanvasConversationGate(
  input: CanvasConversationGateInput,
): CanvasConversationGate {
  const {
    conversationId,
    isConfirmedInSession,
    isPendingConversation,
    hasServerRecord,
    isServerLookupPending,
  } = input;

  // Aucun fil affiché : il n'y a rien à prouver et rien à qui appartenir.
  if (conversationId === null) return 'awaiting_first_message';
  // Les deux preuves sont interchangeables et se contrôlent AVANT le fantôme :
  // la confirmation temps réel arrive pendant que le fantôme est encore là,
  // et c'est précisément la fenêtre où l'ouverture doit devenir possible.
  if (isConfirmedInSession || hasServerRecord) return 'confirmed';
  // Un fantôme non confirmé est un fil qui n'existe que dans cet onglet, et sa
  // lecture serveur est volontairement suspendue tant qu'il l'est (#1284). La
  // sortie est donc « envoie un (nouveau) premier message », jamais une
  // ouverture différée.
  if (isPendingConversation) return 'awaiting_first_message';
  // Une lecture est en vol : l'attente est réelle, la promesse est tenable.
  if (isServerLookupPending) return 'verifying';
  // Ni preuve, ni fantôme, ni lecture en cours. C'est le cas du premier envoi
  // échoué : le fantôme a été retiré au rollback, plus rien ne cherche, et
  // annoncer une vérification serait promettre une ouverture qui n'arrivera pas.
  return 'unverifiable';
}

/**
 * La phrase montrée à l'avocat quand l'ouverture est refusée, ou `null` quand
 * elle est permise.
 *
 * `switch` exhaustif volontaire : un état ajouté à l'union doit casser la
 * compilation ici plutôt que tomber dans un `else` où le refus se changerait en
 * autorisation silencieuse.
 */
export function decrireLeRefusDOuvertureCanvas(gate: CanvasConversationGate): string | null {
  switch (gate) {
    case 'confirmed':
      return null;
    case 'awaiting_first_message':
      return 'Envoyez un premier message pour ouvrir le Canvas';
    case 'verifying':
      return 'Vérification de la conversation en cours — le Canvas s\'ouvrira ensuite';
    case 'unverifiable':
      return 'Conversation inaccessible : le Canvas ne peut pas s\'y ouvrir';
  }
}

/**
 * Les fils dont l'existence a été prouvée, publiés pour les lecteurs hors React.
 *
 * Même mécanique — et mêmes raisons — que `conversationAffichee` : les gestes
 * d'ouverture partent de composants qui ne sont pas montés dans l'assistant (la
 * grille d'un dossier, la coque), et un `useQuery` de détail REST n'est pas
 * lisible hors de React.
 *
 * **Un troisième publieur depuis le 2026-10-07** : le menu d'une pièce de « Mes
 * écritures » (`OuvrirDansLeCanvasAction`) publie le fil que l'avocat choisit
 * dans la liste serveur du dossier, ou celui qu'il vient de créer par
 * `POST /api/conversations` — deux données serveur, que l'écran assistant ne
 * peut pas publier puisqu'il n'est pas monté sur la page d'un dossier.
 *
 * **Deux publieurs historiques, et il faut les deux.** Le pont temps réel
 * (`WebSocketPoolProvider`, monté dans la coque) publie chaque
 * `conversation_confirmed` : il voit la preuve où que soit l'avocat. L'écran
 * assistant publie en plus la preuve tirée de la donnée serveur, la seule qui
 * survive à un rechargement — mais lui ne connaît que le fil affiché.
 *
 * **La valeur GÈLE au départ de l'écran, elle ne s'efface pas.** Une preuve
 * d'existence ne se révoque pas : un fil confirmé le reste pendant que l'avocat
 * consulte un dossier. C'est exactement ce que fait déjà `canvasStage` du
 * porteur, et pour la même raison.
 *
 * C'est un ENSEMBLE et non une dernière valeur : l'avocat peut confirmer A,
 * puis B, puis ouvrir une pièce d'un dossier dans A. Un pointeur unique
 * refuserait A au motif que B est passé après, alors que rien n'a dé-prouvé A.
 *
 * ## Pourquoi DEUX registres, et quand celui-ci se vide
 *
 * `activeSessionsStore.confirmedConversations` et cet ensemble portent le même
 * fait. Le ticket demande de ne pas dupliquer l'état ; l'arbitrage retenu est
 * de garder les deux, et voici ce qui le justifie et ce qui le borne.
 *
 * - **Ils n'ont pas le même lecteur.** Le store est un store React : ses
 *   lecteurs sont des composants. Cet ensemble est lu par des gestes qui
 *   partent HORS de React — `ouvrirLeCanvasPourLAvocat` appelé depuis la grille
 *   d'un dossier, où l'écran assistant n'est pas monté. Faire lire le store
 *   impérativement (`useActiveSessionsStore.getState()`) marcherait pour la
 *   confirmation temps réel, mais **pas** pour la seconde preuve : la donnée
 *   serveur vit dans le cache TanStack, sous une clé qui dépend du cabinet et
 *   de l'utilisateur, et n'est pas atteignable sans contexte React.
 * - **Ils ne portent donc pas exactement le même ensemble.** Le store ne
 *   contient QUE les confirmations temps réel de cette session ; celui-ci
 *   contient en plus les fils prouvés par la donnée serveur, publiés par
 *   l'écran assistant. C'est la réunion des deux preuves, pas une copie.
 * - **Il ne se vide qu'au changement de compte** (`oublierLesFilsConfirmes`,
 *   appelé par `canvasEviction`). Et c'est correct : une preuve d'existence ne
 *   se révoque pas — un fil persisté le reste. La seule chose qui la rend
 *   caduque est un changement d'identité, où les identifiants cessent
 *   d'appartenir au lecteur. Une conversation SUPPRIMÉE laisse donc une entrée
 *   périmée jusqu'à la fin de la session ; l'ouverture ne s'y accroche pas
 *   longtemps, la résidence et le brouillon échouant ensuite sur un fil que le
 *   backend ne connaît plus. Rendre la révocation exacte demanderait de câbler
 *   la suppression de conversation ici — hors périmètre #1331, et sans effet
 *   sur le défaut que cette porte ferme.
 */
const filsConfirmes = new Set<string>();

/** Publie un fil dont l'existence vient d'être prouvée. `null` = rien à dire. */
export function publierLeFilConfirme(conversationId: string | null): void {
  if (conversationId === null) return;
  filsConfirmes.add(conversationId);
}

/**
 * Ce fil a-t-il été prouvé ? Lecture hors React, au moment du geste.
 *
 * Sert de seconde garde aux portes d'ouverture : la première (le rendu du
 * contrôle) peut dater de plusieurs secondes, et `aria-disabled` n'empêche
 * aucun clic.
 */
export function estUnFilConfirme(conversationId: string | null): conversationId is string {
  return conversationId !== null && filsConfirmes.has(conversationId);
}

/**
 * Oublie toutes les preuves publiées.
 *
 * Appelé par l'éviction au changement de compte : ces identifiants
 * appartiennent au cabinet et à l'utilisateur qui les a obtenus, et la coque
 * reste montée à travers la transition — sans cette purge, l'avocat B
 * hériterait des autorisations d'ouverture de l'avocat A.
 */
export function oublierLesFilsConfirmes(): void {
  filsConfirmes.clear();
}

/**
 * Le refus rendu par une porte d'ouverture qui n'a PAS de bouton à annoter —
 * la carte de plan de la frise, par exemple.
 *
 * Elle n'a pas d'état d'avance à afficher : elle apprend le refus au clic, et
 * la spec (#1327, l. 84) exige que le contrôle EXPLIQUE. Un `console.warn` ne
 * s'adresse à personne — l'avocat clique et rien ne bouge. Le cas réel est le
 * rechargement de page : la confirmation temps réel est perdue avec l'onglet et
 * le détail REST peut être encore en vol pendant que la frise réhydratée
 * s'affiche déjà. C'est donc une attente, pas une impasse — d'où la phrase.
 */
export const REFUS_OUVERTURE_FIL_NON_PROUVE =
  'Le Canvas ne peut pas encore s\'ouvrir ici : le serveur n\'a pas confirmé cette '
  + 'conversation. Réessayez dans un instant.';
